import React from 'react';
import styled from 'styled-components';
import { OverlayTrigger, Tooltip } from 'react-bootstrap';
import SecondaryButton from '../../components/common/SecondaryButton';
import { LeadStatus, LeadStatusColors, LeadStatusOrder } from '../../constants/leadStatus';

import {
  DndContext,
  closestCenter,
  PointerSensor,
  useSensor,
  useSensors,
  DragOverlay
} from '@dnd-kit/core';
import { useDroppable, useDraggable } from '@dnd-kit/core';
import LeadInfoModal from './LeadInfoModal';
import { FaInfoCircle } from 'react-icons/fa';
import AddLeadModal from './AddLeadModal';
import ConvertToAccountModal from './ConvertToAccountModal';
import LeadService from '../../services/LeadService';
import CustomerAccountService from '../../services/CustomerAccountService';
import { API_STATUS } from '../../constants/apiStatusCode';

const COLUMN_ORDER = LeadStatusOrder;

const CUSTOMER_EXIT_STATUSES = [
  LeadStatus.QUALIFIED,
  LeadStatus.CONTACTED,
  LeadStatus.FOLLOW_UP,
  LeadStatus.NEW_LEADS,
  LeadStatus.LOST,
];

function shouldUpdateAccountStatus(previousStatus, destStatus) {
  return (
    previousStatus === LeadStatus.CUSTOMER &&
    CUSTOMER_EXIT_STATUSES.includes(destStatus)
  );
}

const CARD_COLORS = LeadStatusColors;

// Add Google Fonts import for Inria Serif
const fontLink = document.createElement('link');
fontLink.href = 'https://fonts.googleapis.com/css2?family=Inria+Serif:wght@400;700&display=swap';
fontLink.rel = 'stylesheet';
document.head.appendChild(fontLink);

const ButtonContainer = styled.div`
  display: flex;
  justify-content: flex-start;
  align-items: center;
  gap: 12px;
  margin: 0 0 20px 24px;
`;

// API Service Function
const fetchLeadsDashboard = async () => {
  try {
    const response = await LeadService.getLeads();
    return response.data;
  } catch (error) {
    console.error('Error fetching leads dashboard:', error);
    throw error;
  }
};

const LoadingContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 200px;
  font-family: 'Inria Serif', serif;
`;

const ErrorContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 200px;
  font-family: 'Inria Serif', serif;
  color: #dc3545;
  text-align: center;
  padding: 20px;
`;

const DashboardContainer = styled.div`
  display: flex;
  gap: 16px;
  padding: 24px;
  background: #f5f6fa;
  min-height: 80vh;
  font-family: 'Inria Serif', serif;
`;

const ColumnBase = styled.div`
  background: #fff;
  flex: 1;
  border-radius: 8px;
  padding: 12px;
  min-width: 220px;
  display: flex;
  flex-direction: column;
  max-height: 80vh;
  overflow-y: auto;
  box-shadow: 0 1px 4px rgba(0,0,0,0.04);
`;

const LeadCardBase = styled.div`
  background: ${({ status }) => CARD_COLORS[status] || '#fafbfc'};
  border-radius: 8px;
  border: 1px solid #e9ecef;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.06);
  margin-bottom: 10px;
  padding: 12px 12px 10px;
  cursor: grab;
  font-family: 'Inria Serif', serif;
  color: #212529;
`;

/* Primary line — matches dashboard table body: serif, prominent */
const NameLabel = styled.div`
  font-family: 'Inria Serif', serif;
  font-size: 1rem;
  line-height: 1.35;
  color: #1a1a1a;
  margin-bottom: 6px;
`;

/* Secondary line — smaller, muted (like company under name on dashboard) */
const CompanyLabel = styled.div`
  font-family: 'Inria Serif', serif;
  font-size: 0.8125rem;
  font-weight: 400;
  line-height: 1.35;
  color: #6c757d;
  border-bottom: 1px solid #dee2e6;
  padding-bottom: 6px;
  margin-bottom: 8px;
`;

const CardLabel = styled.div`
  font-family: 'Inria Serif', serif;
  font-size: 1rem;
  line-height: 1.4;
  margin-bottom: 6px;
`;

const Column = React.forwardRef((props, ref) => <ColumnBase ref={ref} {...props} />);
const LeadCard = React.forwardRef(({ status, ...props }, ref) => {
  return <LeadCardBase as="div" ref={ref} status={status} {...props} />;
});

const ColumnTitle = styled.h5`
  text-align: center;
  margin: 0 0 10px 0;
  padding-bottom: 6px;
  border-bottom: 2px solid #e0e0e0;
  color: #333;
  font-weight: bold;
  font-family: 'Inria Serif', serif;
  font-size: 1rem;
  line-height: 1.25;
`;

const CardList = styled.div`
  min-height: 40px;
`;

function groupLeadsByStatus(leads) {
  const grouped = {};
  COLUMN_ORDER.forEach(status => {
    grouped[status] = [];
  });
  leads.forEach(lead => {
    if (grouped[lead.leadsStatus]) {
      grouped[lead.leadsStatus].push(lead);
    }
  });
  return grouped;
}

function DroppableColumn({ status, children }) {
  const { setNodeRef } = useDroppable({ id: status });
  return (
    <Column ref={setNodeRef}>
      <ColumnTitle>{status}</ColumnTitle>
      <CardList>{children}</CardList>
    </Column>
  );
}

function DraggableLeadCard({ lead, status, onInfoClick }) {
  const { attributes, listeners, setNodeRef, transform, isDragging } = useDraggable({ id: lead.leadId.toString() });
  return (
    <LeadCard
      ref={setNodeRef}
      status={status}
      data-testid={`lead-card-${lead.leadId}`}
      style={{
        userSelect: 'none',
        opacity: isDragging ? 0.5 : 1,
        transform: transform ? `translate3d(${transform.x}px, ${transform.y}px, 0)` : undefined,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
      }}
    >
      <div
        style={{ flex: 1, cursor: 'grab', display: 'flex', flexDirection: 'column' }}
        {...attributes}
        {...listeners}
      >
        <NameLabel>{lead.name}</NameLabel>
        {lead.company ? <CompanyLabel>{lead.company}</CompanyLabel> : null}
        {lead.leadsStatus === LeadStatus.FOLLOW_UP && lead.followupDate && (
          <CardLabel style={{ color: '#b26a00' }}>
            <b>Follow up Date:</b> {formatUtcTextToISTText(lead.followupDate)}
          </CardLabel>
        )}
        {lead.leadsStatus === LeadStatus.CONTACTED && lead.contactOn && (
          <CardLabel style={{ color: '#007b83' }}>
            <b>Contacted On:</b> {formatUtcTextToISTText(lead.contactOn)}
          </CardLabel>
        )}
      </div>
      <button
        style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#007bff', fontSize: 20, marginLeft: 8 }}
        onClick={e => { e.stopPropagation(); onInfoClick(lead); }}
        title="More Info"
      >
        <FaInfoCircle />
      </button>
    </LeadCard>
  );
}

function formatUtcTextToISTText(input) {
  if (!input) return '';

  const match = input.match(
    /(\w+) (\d+) (\d{4}) at (\d{1,2}):(\d{2}) (AM|PM)/
  );
  if (!match) return input;

  const [, monthName, day, year, hour12, minute, ampm] = match;

  const monthIndex = new Date(Date.parse(monthName + " 1, 2000")).getMonth();

  let hour24 = parseInt(hour12);
  if (ampm === 'PM' && hour24 !== 12) hour24 += 12;
  if (ampm === 'AM' && hour24 === 12) hour24 = 0;

  const utcDate = new Date(Date.UTC(
    parseInt(year),
    monthIndex,
    parseInt(day),
    hour24,
    parseInt(minute)
  ));

  // Convert to IST
  const ist = new Date(
    utcDate.toLocaleString('en-US', { timeZone: 'Asia/Kolkata' })
  );

  const month = ist.toLocaleString('en-US', { month: 'long' });
  const d = ist.getDate();
  const y = ist.getFullYear();

  let h = ist.getHours();
  const m = String(ist.getMinutes()).padStart(2, '0');

  const ampmOut = h >= 12 ? 'PM' : 'AM';
  h = h % 12 || 12;

  return `${month} ${d} ${y} at ${h}:${m} ${ampmOut}`;
}

const LeadsWidgets = ({ onStatusChange, onLeadUpdate }) => {
  const [leads, setLeads] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState(null);
  const [activeLead, setActiveLead] = React.useState(null);
  const [modalOpen, setModalOpen] = React.useState(false);
  const [addLeadModalOpen, setAddLeadModalOpen] = React.useState(false);
  const [convertModalOpen, setConvertModalOpen] = React.useState(false);
  const [convertLead, setConvertLead] = React.useState(null);
  const sensors = useSensors(useSensor(PointerSensor));

  // Fetch leads data on component mount
  React.useEffect(() => {
    const loadLeads = async () => {
      try {
        setLoading(true);
        setError(null);
        const response = await fetchLeadsDashboard(); 
        const apiData = response.data.leadDetails || [];
        console.log('Raw API data:', apiData);
        
        // Transform the data to match the expected format
        const transformedLeads = apiData.map(lead => ({
          name: lead.name,
          leadId: lead.leadId, 
          company: lead.company,
          leadsStatus: lead.leadsStatus, // Transform status
          email: lead.email,
          phoneNumber: lead.phoneNumber,
          notes: lead.notes,
          followupDate: lead.followupDate,
          contactOn: lead.contactOn
        }));
        
        console.log('Transformed leads:', transformedLeads);
        setLeads(transformedLeads);
      } catch (err) {
        setError('Failed to load leads. Please try again.');
        console.error('Error loading leads:', err);

      } finally {
        setLoading(false);
      }
    };

    loadLeads();
  }, []);

  // Group leads by status for stable rendering
  const groupedLeads = React.useMemo(() => groupLeadsByStatus(leads), [leads]);

  const findLeadById = (id) => leads.find(lead => lead.leadId.toString() === id);
  const findLeadIndexById = (id) => leads.findIndex(l => l.leadId === id);


  const handleDragStart = (event) => {
    const { active } = event;
    setActiveLead(findLeadById(active.id));
  };

  const handleDragEnd = async (event) => {
    const { active, over } = event;
    setActiveLead(null);
    if (!over) return;
    const sourceId = active.id;
    const destStatus = over.id;
    const leadIndex = findLeadIndexById(sourceId);
    if (leadIndex == -1) return;

    const lead = leads[leadIndex];
    if (!lead || lead.leadsStatus === destStatus) return;


    // Restriction: Can't move directly from New Leads to Customer
    if (lead.leadsStatus === LeadStatus.NEW_LEADS && destStatus === LeadStatus.CUSTOMER) {
      alert('You must qualify a lead before moving to Customer!');
      return;
    }
    // Restriction: Can only move to Customer from Qualified
    if (destStatus === LeadStatus.CUSTOMER && lead.leadsStatus !== LeadStatus.QUALIFIED) {
      alert('Only Qualified leads can be moved to Customer!');
      return;
    }

    if (destStatus === LeadStatus.CUSTOMER && lead.leadsStatus === LeadStatus.QUALIFIED) {
      setConvertLead(lead);
      setConvertModalOpen(true);
      return;
    }

    const previousStatus = lead.leadsStatus;
    lead.leadsStatus = destStatus;
    const newLeads = [...leads];
    newLeads[leadIndex] = lead;
    setLeads(newLeads);

    const leadData = {
        leadId: lead.leadId,
        leadsStatus: lead.leadsStatus || LeadStatus.NEW_LEADS,
        notes: lead.notes,
      };

    // make the server call to update the lead
    try {
      const response = await LeadService.updateLead(leadData);
      if (response.data.success) {
        if (shouldUpdateAccountStatus(previousStatus, destStatus)) {
          try {
            await CustomerAccountService.updateAccountStatus({
              leadId: lead.leadId,
              status: destStatus,
            });
          } catch (accountErr) {
            console.error('Error updating account status:', accountErr);
          }
        }
         reloadDashboard();
      }
    } catch (error) {
      console.error('Error updating lead:', error);
    }
  };

  const closeConvertModal = () => {
    setConvertModalOpen(false);
    setConvertLead(null);
  };

  const handleConvertComplete = () => {
    reloadDashboard();
    onStatusChange?.();
    onLeadUpdate?.();
  };

  const handleInfoClick = (lead) => {
    setActiveLead(lead);
    setModalOpen(true);
  };

  const updateLeadsInfo = (updatedLead) => {
    setLeads(leads.map(l => l.leadId === updatedLead.leadId ? updatedLead : l));
    reloadDashboard();
    setModalOpen(false);
    setActiveLead(null);
  };

  const cancelLeadsUpdate = () => {
    setModalOpen(false);
    setActiveLead(null);
  };

  // Add Lead Modal

  const handleAddLeadClick = () => {
    setAddLeadModalOpen(true);
  };

  const reloadDashboard = async () => {
    const response = await fetchLeadsDashboard();
    const apiData = response.data.leadDetails || [];
    const transformedLeads = apiData.map(lead => ({
      name: lead.name,
      leadId: lead.leadId, 
      company: lead.company,
      leadsStatus: lead.leadsStatus,
      email: lead.email,
      phoneNumber: lead.phoneNumber,
      notes: lead.notes,
      followupDate: lead.followupDate,
      contactOn: lead.contactOn
    }));
    setLeads(transformedLeads);
  };

  const addLeadsInfo = async (newLead) => {
    setAddLeadModalOpen(false);
    reloadDashboard();
    
  };
  
  const handleAddLeadCancel = () => {
    setAddLeadModalOpen(false);
  };

  const isValidEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const handleImportCSV = async () => {
    // Create a file input element
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = '.csv';
    input.onchange = async (e) => {
      const file = e.target.files[0];
      if (!file) return;

      const reader = new FileReader();
      reader.onload = async (event) => {
        try {
          const text = event.target.result;
          const lines = text.split('\n').filter(line => line.trim());
          if (lines.length < 2) {
            alert("CSV file must contain at least a header row and one data row");
            return;
          }

          const headers = lines[0].split(',').map(h => h.trim().toLowerCase());

          // Find column indices - flexible matching
          const nameIndex = headers.findIndex(h =>
            h.includes('name') || h.includes('contact name')
          );
          const emailIndex = headers.findIndex(h =>
            h.includes('email')
          );
          const phoneIndex = headers.findIndex(h =>
            h.includes('phone') || h.includes('number')
          );
          const companyIndex = headers.findIndex(h =>
            h.includes('company') || h.includes('organization')
          );

          if (emailIndex === -1 || phoneIndex === -1 || nameIndex === -1 || companyIndex === -1) {
            alert("CSV must contain 'name','email','phoneNumber' and 'company' columns");
            return;
          }

          // Parse CSV and build payload
          const bulkLeadPayload = [];
          const newContacts = [];

          for (let i = 1; i < lines.length; i++) {
            const values = lines[i].split(',').map(v => v.trim().replace(/^"|"$/g, ''));
            const name = values[nameIndex] || '';
            const email = values[emailIndex] || '';
            const phoneNumber = values[phoneIndex] || '';
            const firstName = name.split(' ')[0] || name;
            const lastName = name.split(' ').slice(1).join(' ') || 'N.A';
            const company = values[companyIndex] || '';

            // Clean phone number (remove spaces, keep digits and +)
            const cleanPhoneNumber = phoneNumber.replace(/\D/g, '');

            // Validate email and phone number
            if (email && cleanPhoneNumber && isValidEmail(email)) {
              // Build API payload
              bulkLeadPayload.push({
                firstName: firstName || '',
                lastName: lastName || '',
                email: email,
                whatsAppNo: cleanPhoneNumber,
                company: company,
              });
            }
          }

          if (bulkLeadPayload.length === 0) {
            alert("No valid contacts found in CSV. Please check the format.");
            return;
          }

          // Call insertBulkLead API
          try {
            const res = await LeadService.insertBulkLead(bulkLeadPayload);
            if (
              res.data.statusCode === API_STATUS.SUCCESS ||
              res.data.statusCode === API_STATUS.CREATED
            ) {
              reloadDashboard();
              alert(
                res?.data?.message ||
                `Successfully imported ${bulkLeadPayload.length} lead(s).`
              );
            } else {
              alert(res?.data?.message || "Failed to import leads from CSV");
            }
          } catch (apiError) {
            console.error("Error calling insertBulkLead API", apiError);
            alert(apiError?.response?.data?.message || "Failed to import leads. Please try again.");
          }
        } catch (error) {
          console.error("Error parsing CSV", error);
          alert("Failed to parse CSV file. Please check the format.");
        }
      };
      reader.readAsText(file);
    };
    input.click();
  };

  const handleDownloadSampleCsv = () => {
    const header = 'Name,PhoneNumber(Without country code),Email,Company';
    const exampleRow = 'Jane Doe,1234567890,jane.doe@example.com,Acme Inc';
    const csv = `${header}\r\n${exampleRow}\r\n`;
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = 'leads_import_sample.csv';
    link.rel = 'noopener';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  // Show loading state
  if (loading) {
    return (
      <LoadingContainer>
        <div>Loading leads...</div>
      </LoadingContainer>
    );
  }

  // Show error state
  if (error) {
    return (
      <ErrorContainer>
        <div>
          <div>{error}</div>
          <button 
            onClick={() => window.location.reload()} 
            style={{ marginTop: '10px', padding: '8px 16px', cursor: 'pointer' }}
          >
            Retry
          </button>
        </div>
      </ErrorContainer>
    );
  }

  return (
    <>
      <ButtonContainer>
        <SecondaryButton
          onClick={handleAddLeadClick}
          style={{ padding: '6px 12px', fontSize: '0.8rem' }}
        >
          Add Leads
        </SecondaryButton>
        <OverlayTrigger
          placement="top"
          delay={{ show: 250, hide: 100 }}
          overlay={<Tooltip id="tooltip-import-leads-csv">Import leads via csv file</Tooltip>}
        >
          <span className="d-inline-block">
            <SecondaryButton
              onClick={handleImportCSV}
              style={{ padding: '6px 12px', fontSize: '0.8rem' }}
            >
              Import Leads
            </SecondaryButton>
          </span>
        </OverlayTrigger>
        <OverlayTrigger
          placement="top"
          delay={{ show: 250, hide: 100 }}
          overlay={
            <Tooltip id="tooltip-sample-csv">
              Download sample CSV file for bulk lead import
            </Tooltip>
          }
        >
          <span className="d-inline-block">
            <SecondaryButton
              type="button"
              onClick={handleDownloadSampleCsv}
              style={{ padding: '6px 12px', fontSize: '0.8rem' }}
            >
              Download Sample CSV
            </SecondaryButton>
          </span>
        </OverlayTrigger>
      </ButtonContainer>

      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragStart={handleDragStart}
        onDragEnd={handleDragEnd}
      >
        <DashboardContainer>
          {COLUMN_ORDER.map((status) => (
            <DroppableColumn status={status} key={status}>
              {groupedLeads[status].map((lead) => (
                <DraggableLeadCard
                  lead={lead}
                  status={status}
                  key={lead.leadId}
                  onInfoClick={handleInfoClick}
                />
              ))}
            </DroppableColumn>
          ))}
        </DashboardContainer>
        <DragOverlay>
          {activeLead ? (
            <LeadCard status={activeLead.leadsStatus} style={{ opacity: 0.7 }}>
              <div>
                <b>{activeLead.name}</b>
              </div>
              <div>{activeLead.company}</div>
            </LeadCard>
          ) : null}
        </DragOverlay>
      </DndContext>

      <LeadInfoModal
        isOpen={modalOpen}
        onRequestClose={cancelLeadsUpdate}
        lead={activeLead}
        onSave={updateLeadsInfo}
        onCancel={cancelLeadsUpdate}
      />
      <AddLeadModal
        isOpen={addLeadModalOpen}
        onRequestClose={handleAddLeadCancel}
        onSave={addLeadsInfo}
        onCancel={handleAddLeadCancel}
      />
      <ConvertToAccountModal
        show={convertModalOpen}
        lead={convertLead}
        onClose={closeConvertModal}
        onComplete={handleConvertComplete}
      />
    </>
  );
};

export default LeadsWidgets; 